import { Autocomplete, CircularProgress, TextField, type AutocompleteRenderInputParams } from "@mui/material";
import { optionKey, optionLabel, sameOption } from "./EntityPicker.helpers";
import type { EntityPickerProps } from "./EntityPicker.types";

/**
 * The one picker for choosing records or entries of a searchable list (companies, seasons, roles,
 * team members…). A thin, typed MUI Autocomplete: equality by key, a spinner while `loading`
 * (which never disables: pass `disabled` for that), MUI's default chips when `multiple`, and no
 * Grid or width of its own — the caller lays it out with `sx`.
 */
export function EntityPicker<T>(props: EntityPickerProps<T>) {
	// KeyProps<T> is a conditional type, so read its one member through a plain shape.
	const getKey = (props as { getOptionKey?: (option: T) => string }).getOptionKey;

	const renderInput = (params: AutocompleteRenderInputParams) => (
		<TextField
			{...params}
			label={props.hideLabel ? undefined : props.label}
			variant={props.variant}
			autoFocus={props.autoFocus}
			placeholder={props.placeholder}
			required={props.required}
			error={props.error}
			helperText={props.helperText}
			name={props.name}
			inputRef={props.inputRef}
			onBlur={props.onBlur}
			sx={props.inputSx}
			slotProps={{
				input: {
					...params.InputProps,
					endAdornment: (
						<>
							{props.loading ? <CircularProgress color="inherit" size={20} /> : null}
							{params.InputProps.endAdornment}
						</>
					),
				},
				htmlInput: {
					...params.inputProps,
					"aria-label": props.hideLabel ? props.label : undefined,
					readOnly: props.searchable === false ? true : undefined,
				},
				inputLabel: props.shrinkLabel ? { ...params.InputLabelProps, shrink: true } : params.InputLabelProps,
			}}
		/>
	);

	const shared = {
		id: props.id,
		sx: props.sx,
		// MUI types options as mutable; it never writes to them.
		options: props.options as T[],
		getOptionLabel: (option: T) => optionLabel(option, props.getOptionLabel),
		getOptionKey: (option: T) => optionKey(option, getKey),
		isOptionEqualToValue: (option: T, value: T) =>
			sameOption(option, value, { getOptionKey: getKey, isEqual: props.isEqual }),
		loading: props.loading,
		loadingText: props.loadingText,
		disabled: props.disabled,
		fullWidth: props.fullWidth,
		size: props.size,
		groupBy: props.groupBy,
		onOpen: props.onOpen ? () => props.onOpen?.() : undefined,
		disableCloseOnSelect: props.disableCloseOnSelect,
		blurOnSelect: props.blurOnSelect,
		renderOption: props.renderOption,
		noOptionsText: props.noOptionsText,
		renderInput,
	};

	if (props.multiple) {
		return (
			<Autocomplete<T, true, false, false>
				{...shared}
				multiple
				limitTags={props.limitTags}
				// Passed through as is: a copy per render is a new value to MUI, which then clears the typed text.
				value={props.value as T[]}
				onChange={(event, value) => {
					if (props.stopPropagation) event.stopPropagation();
					props.onChange(value);
				}}
			/>
		);
	}

	return (
		<Autocomplete<T, false, boolean, false>
			{...shared}
			disableClearable={props.disableClearable}
			value={props.value}
			onChange={(event, value) => {
				if (props.stopPropagation) event.stopPropagation();
				if (props.disableClearable) {
					if (value !== null) props.onChange(value);
				} else {
					props.onChange(value);
				}
			}}
		/>
	);
}
